import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CryptoJS from 'crypto-js';
import { useIntl } from 'react-intl';

import ModalWrapper from 'components/Modals/ModalWrapper';
import BoxInputField from 'components/Common/Inputs/BoxInputField';
import LocalisedMessage from 'components/Common/LocalisedMessage';
import {
  sendVerificationPin,
  verifyCustomerAndPin
} from 'actions';
import pinType from 'common/pinType';

const PinVerificationModal = ({ authenticatingVerificationPin, requestingVerificationPin, pinVerificationMessages, pinRequestStatus, dispatch }) => {
  const [pin, setPin] = useState(null);
  const [visible, setIsVisible] = React.useState(false);
  const intl = useIntl();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const authentalinkId = queryParams.get('id');

  const resendPinVerifcation = () => {
    dispatch(sendVerificationPin(pinType.ElectornicSignaturePin, authentalinkId));
  };

  const onPinVerificationConfirm = () => {
    dispatch(verifyCustomerAndPin(authentalinkId, pin));
  };

  useEffect(() => {
    dispatch(sendVerificationPin(pinType.ElectornicSignaturePin, authentalinkId));
  }, [location]);

  useEffect(() => {
    if (!pinVerificationMessages) {
      setIsVisible(false);
      return;
    }
    setIsVisible(true);
  }, [pinVerificationMessages]);

  const error = visible && <span className={ `pin-status ${ pinRequestStatus ? 'success' : '' }` }><FontAwesomeIcon icon={ `${ pinRequestStatus ? 'check' : 'times' }` } /><span className="error-msg">{ pinVerificationMessages }</span></span>;

  const content = <span>
    <p><strong><LocalisedMessage id="pinVerificationText.Line.1" /></strong></p>
    <p><LocalisedMessage id="pinVerificationText.Line.2" /></p>
    <p>
      <button
        className={ `btn btn-link ${ requestingVerificationPin ? 'btn-active' : '' }` }
        type="button"
        onClick={ () => resendPinVerifcation() }
      >
        <strong> { !requestingVerificationPin && `${ intl.formatMessage({ id: 'authentalink.pinConfirmation.ResendEmailText' }) }` }</strong>
      </button>
    </p>
    <BoxInputField id="verification-pin" className="pin-box" callback={ setPin } fieldLength={ 6 } disabled={ false } fieldSeperator={ [1, 3] } />
  </span>;

  return (
    <ModalWrapper
      title={ <LocalisedMessage id="authentalink.pinVerification.title" /> }
      closeButtonName={ intl.formatMessage({ id: 'pinVerificationText.title' }) }
      className="authentalink-verification"
      content={ content }
      showCancel={ false }
      showClose={ false }
      btnConfirmTitle={ intl.formatMessage({ id: 'authentalink.pinVerification.buttonText' }) }
      disableConfirm={ pin === null || pin.length !== 6 || authenticatingVerificationPin }
      onConfirm={ () => onPinVerificationConfirm() }
      btnConfirmClass={ `${ authenticatingVerificationPin ? 'btn-active' : '' }` }
      footer={ pinVerificationMessages !== null && error }
    />
  );
};

PinVerificationModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  authenticatingVerificationPin: PropTypes.bool,
  requestingVerificationPin: PropTypes.bool,
  pinVerificationMessages: PropTypes.string,
  pinRequestStatus: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired
};

PinVerificationModal.defaultProps = {
  authenticatingVerificationPin: false,
  requestingVerificationPin: false,
  pinVerificationMessages: null,
  pinRequestStatus: false
};

function mapStateToProps(state) {
  const {
    app: {
      customerData,
      key
    },
    authentaLink: { authenticatingVerificationPin, pinVerificationMessages, requestingVerificationPin, pinRequestStatus }
  } = state;

  let customerDetails = {};
  if (customerData && key) {
    try {
      const decryptedArray = CryptoJS.AES.decrypt(customerData, key);
      const decryptedText = decryptedArray.toString(CryptoJS.enc.Utf8);
      customerDetails = JSON.parse(decryptedText);
    } catch (error) {
      console.error('Error decrypting customer data:', error);
    }
  }

  return {
    email: customerDetails && customerDetails.CustomerEmailAddress,
    authenticatingVerificationPin,
    pinVerificationMessages,
    requestingVerificationPin,
    pinRequestStatus
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PinVerificationModal);
